// src/components/modals/HoldOrdersModal.jsx
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import toast from 'react-hot-toast'
import { 
  X, 
  Search, 
  Calendar, 
  Clock, 
  User, 
  MapPin, 
  Truck, 
  Eye,
  Trash2,
  Loader2,
  CheckCircle,
  AlertTriangle
} from 'lucide-react'
import { formatUTCDate } from '../../utils/dateUtils'
import OrderService from '../../services/orderService'
import { getDisplayOrderType, convertAPIToUIOrderType } from '../../utils/orderTypeUtils'
import ConfirmDialog from '../ui/ConfirmDialog'

const HoldOrdersModal = ({ 
  isOpen, 
  onClose, 
  branchId,
  currentOrderType = null, // Current order type for filtering
  currentTable = null, // Current table for filtering
  onOrderSelect,
  onOrderDelete
}) => {
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [orderTypeFilter, setOrderTypeFilter] = useState('')
  const [error, setError] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(null)
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    order: null
  })

  const fetchPendingOrders = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log('🔍 Fetching pending orders for branch:', branchId)
      console.log('🔍 Current context - Order Type:', currentOrderType, 'Table:', currentTable)
      
      // Build additional filter parameters based on current context
      const additionalParams = {
        limit: 50, // Fetch more orders for hold functionality
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }
      
      // Don't filter by currentOrderType - show all pending orders
      // Users can use the order type filter dropdown if they want to filter
      
      // Only add table filter for dine-in orders if specifically selected
      if (currentOrderType === 'dine-in' && currentTable?._id) {
        additionalParams.table = currentTable._id
      }
      
      const response = await OrderService.getPendingOrders(branchId, additionalParams)
      
      if (response?.success) {
        const ordersData = response.data?.orders || []
        setOrders(ordersData)
        console.log('✅ Fetched', ordersData.length, 'pending orders with filters:', additionalParams)
        
        // Debug: Log first order to see structure
        if (ordersData.length > 0) {
          console.log('🔍 First order structure:', ordersData[0])
          console.log('🔍 Table data:', ordersData[0].table)
          console.log('🔍 Order type:', ordersData[0].orderType)
        }
      } else {
        throw new Error(response?.message || 'Failed to fetch orders')
      }
    } catch (error) {
      console.error('❌ Error fetching pending orders:', error)
      setError(error.message || 'Failed to load pending orders')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  const filterOrders = () => {
    let filtered = [...orders]

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(order => 
        order.orderNumber?.toLowerCase().includes(searchLower) ||
        order.customer?.name?.toLowerCase().includes(searchLower) ||
        order.customer?.mobileNumber?.includes(searchTerm)
      )
    }

    // Date filter
    if (dateFilter) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.orderDate || order.createdAt)
        const filterDate = new Date(dateFilter)
        return orderDate.toDateString() === filterDate.toDateString()
      })
    }

    // Order type filter
    if (orderTypeFilter) {
      filtered = filtered.filter(order => {
        // Normalize both order type and filter to UI format for comparison
        const orderTypeUI = order.orderType?.includes('_') ? convertAPIToUIOrderType(order.orderType) : order.orderType;
        return orderTypeUI === orderTypeFilter;
      });
    }

    setFilteredOrders(filtered)
  }

  // Fetch pending orders when modal opens or context changes
  useEffect(() => {
    if (isOpen && branchId) {
      fetchPendingOrders()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, branchId, currentOrderType, currentTable])

  // Filter orders when search term or filters change
  useEffect(() => {
    filterOrders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchTerm, dateFilter, orderTypeFilter])

  const handleOrderSelect = (order) => {
    try {
      console.log('📋 Loading order into POS:', order.orderNumber)
      
      // Format order data for the cart
      const cartData = OrderService.formatOrderForCart(order)
      
      // Call the parent callback with formatted data
      if (onOrderSelect) {
        onOrderSelect(cartData)
      }
      
      // Close modal
      onClose()
    } catch (error) {
      console.error('❌ Error loading order:', error)
      setError('Failed to load order into POS')
    }
  }

  const handleOrderDelete = async (order, event) => {
    event.stopPropagation()
    
    // Open confirmation dialog
    setConfirmDialog({
      isOpen: true,
      order: order
    })
  }

  const confirmOrderDelete = async () => {
    const order = confirmDialog.order
    if (!order) return

    try {
      setDeleteLoading(order._id)
      
      console.log('🗑️ Permanently deleting order:', order.orderNumber)
      
      await OrderService.deleteOrder(order._id)
      
      // Remove from local state
      setOrders(prev => prev.filter(o => o._id !== order._id))
      
      // Call parent callback if provided
      if (onOrderDelete) {
        onOrderDelete(order)
      }
      
      // Close confirmation dialog
      setConfirmDialog({ isOpen: false, order: null })
      
      // Show success toast
      toast.success(`Order ${order.orderNumber} deleted successfully!`, {
        icon: '🗑️',
        duration: 3000
      })
      
      console.log('✅ Order deleted permanently')
      
    } catch (error) {
      console.error('❌ Error deleting order:', error)
      
      // Close confirmation dialog first
      setConfirmDialog({ isOpen: false, order: null })
      
      // Show user-friendly error message based on the error
      const errorMessage = error.message || 'Failed to delete order'
      
      if (errorMessage.includes('some associated barista orders are not in PENDING status')) {
        toast.error('Cannot delete order: Some items are being prepared by barista or already completed', {
          icon: '⚠️',
          duration: 4000
        })
      } else if (errorMessage.includes('barista orders')) {
        toast.error('Cannot delete order: Order has active barista preparations', {
          icon: '⚠️',
          duration: 4000
        })
      } else {
        toast.error(`Failed to delete order: ${errorMessage}`, {
          icon: '❌',
          duration: 4000
        })
      }
    } finally {
      setDeleteLoading(null)
    }
  }

  const closeConfirmDialog = () => {
    setConfirmDialog({ isOpen: false, order: null })
  }

  const clearFilters = () => {
    setSearchTerm('')
    setDateFilter('')
    setOrderTypeFilter('')
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getOrderTypeIcon = (orderType) => {
    // Convert API format to UI format for consistent checking
    const uiOrderType = orderType?.includes('_') ? convertAPIToUIOrderType(orderType) : orderType;
    
    switch (uiOrderType) {
      case 'delivery':
        return <Truck className="h-4 w-4 text-blue-600" />
      case 'takeaway':
        return <MapPin className="h-4 w-4 text-orange-600" />
      default:
        return <Clock className="h-4 w-4 text-green-600" />
    }
  }

  const getOrderTypeLabel = (orderType) => {
    return getDisplayOrderType(orderType || 'dine-in')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Hold Orders</h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredOrders.length} pending order{filteredOrders.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by order number, customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Order Type Filter */}
            <select
              value={orderTypeFilter}
              onChange={(e) => setOrderTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Types</option>
              <option value="dine-in">Dine-in</option>
              <option value="takeaway">Takeaway</option>
              <option value="delivery">Delivery</option>
            </select>

            {/* Date Filter */}
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />

            {/* Clear Filters */}
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-200 rounded-lg flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span className="text-red-700">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
              <span className="ml-2 text-gray-600">Loading pending orders...</span>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Orders</h3>
              <p className="text-gray-600">
                {orders.length === 0 
                  ? "There are no pending orders at the moment."
                  : "No orders match your current filters."
                }
              </p>
              {orders.length > 0 && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredOrders.map((order) => (
                <div
                  key={order._id}
                  onClick={() => handleOrderSelect(order)}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer group"
                >
                  {/* Order Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      {getOrderTypeIcon(order.orderType)}
                      <span className="font-medium text-gray-900">
                        {order.orderNumber}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleOrderSelect(order)}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                        title="Load Order"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => handleOrderDelete(order, e)}
                        disabled={deleteLoading === order._id}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Delete Order Permanently"
                      >
                        {deleteLoading === order._id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="flex items-center space-x-2 mb-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">
                      {order.customer?.name || 'Walking Customer'}
                    </span>
                    {order.customer?.mobileNumber && (
                      <span className="text-xs text-gray-500">
                        ({order.customer.mobileNumber})
                      </span>
                    )}
                  </div>

                  {/* Order Details */}
                  <div className="space-y-1 mb-3">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {formatUTCDate(order.orderDate || order.createdAt, 'MMM dd, yyyy HH:mm')}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {getOrderTypeLabel(order.orderType)}
                        {order.deliveryProvider && ` (${order.deliveryProvider})`}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {order.orderItems?.length || 0} item{(order.orderItems?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Table info for dine-in orders */}
                    {(order.orderType === 'dine_in' || order.orderType === 'dine-in') && order.table && (() => {
                      // Handle different table data structures
                      const tableNumber = typeof order.table === 'string' 
                        ? order.table 
                        : (order.table.number || order.table.tableNumber || order.table._id);
                      const tableCapacity = typeof order.table === 'object' ? order.table.capacity : null;
                      
                      console.log('🪑 Rendering table for order:', order.orderNumber, {
                        orderType: order.orderType,
                        table: order.table,
                        tableNumber,
                        tableCapacity
                      });

                      return (
                        <div className="flex items-center space-x-2 mt-1 p-2 bg-orange-50 rounded border border-orange-200">
                          <MapPin className="h-4 w-4 text-orange-600" />
                          <span className="text-sm font-medium text-orange-800">
                            Table {tableNumber}
                          </span>
                          {tableCapacity && (
                            <span className="text-xs text-orange-600">
                              • {tableCapacity} seats
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Total Amount */}
                  <div className="border-t border-gray-100 pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Amount:</span>
                      <span className="text-lg font-bold text-orange-600">
                        {formatCurrency(order.orderPayment?.totalAmount || 0)}
                      </span>
                    </div>
                  </div>

                  {/* Hover indicator */}
                  <div className="mt-3 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs text-orange-600 font-medium">
                      Click to load into POS
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <button
              onClick={fetchPendingOrders}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <span>Refresh</span>
            </button>
            
            <button
              onClick={onClose}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmOrderDelete}
        title="Delete Order"
        message={`Are you sure you want to permanently delete order ${confirmDialog.order?.orderNumber}? This action cannot be undone and will remove all order data from the system.`}
        confirmText="Delete Order"
        cancelText="Cancel"
        type="danger"
        icon={<Trash2 className="h-6 w-6" />}
      />
    </div>
  )
}

HoldOrdersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  branchId: PropTypes.string.isRequired,
  currentOrderType: PropTypes.string,
  currentTable: PropTypes.object,
  onOrderSelect: PropTypes.func.isRequired,
  onOrderDelete: PropTypes.func
}

export default HoldOrdersModal
